﻿import type { MemberDetail } from "@/types.ts";
import { AdminButton, AdminFormField, AdminInfoRow, AdminSelect, AdminTextarea, AdminTextInput } from "@/components/admin";
import { formatDateTime } from "@/lib/format";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { fetchMemberDeletionPreview, type AdminMutationImpactPreview } from "@/lib/api";
interface AccountDetailPanelProps {
    detail: MemberDetail | null;
    loading: boolean;
    saving: boolean;
    reasonCode: string;
    reason: string;
    auditMessage: string | null;
    onClose: () => void;
    onReasonCodeChange: (value: string) => void;
    onReasonChange: (value: string) => void;
    onStatusChange: (status: string) => void;
    onDelete: (confirmationText: string) => void;
}
const userTypeLabels: Record<string, string> = {
    SENIOR: "Senior",
    GUARDIAN: "Keeper",
};
const accountTypeLabels: Record<string, string> = {
    USER_ACCOUNT: "사용자 계정",
};
const yesNoLabels: Record<string, string> = {
    Y: "연결",
    N: "미연결",
};
const statusLabels: Record<string, string> = {
    ACTIVE: "활성",
    INACTIVE: "비활성",
    DELETED: "삭제",
};
const statusStyleMap: Record<string, {
    bg: string;
    text: string;
    border: string;
    dot: string;
}> = {
    ACTIVE: {
        bg: "bg-emerald-50",
        text: "text-emerald-700",
        border: "border-emerald-200",
        dot: "bg-emerald-500",
    },
    INACTIVE: {
        bg: "bg-slate-50",
        text: "text-slate-500",
        border: "border-slate-200",
        dot: "bg-slate-400",
    },
    DELETED: {
        bg: "bg-rose-50",
        text: "text-rose-700",
        border: "border-rose-200",
        dot: "bg-rose-500",
    },
};
const AccountDetailPanel = ({ detail, loading, saving, reasonCode, reason, auditMessage, onClose, onReasonCodeChange, onReasonChange, onStatusChange, onDelete, }: AccountDetailPanelProps) => {
    const navigate = useNavigate();
    const [preview, setPreview] = useState<AdminMutationImpactPreview | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [confirmationText, setConfirmationText] = useState("");
    useEffect(() => {
        setPreview(null);
        setConfirmationText("");
    }, [detail?.userAccountId]);
    if (!detail && loading) {
        return (<div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 min-h-[16.25rem] flex items-center justify-center">
        <span className="text-xs text-slate-400">계정 상세를 불러오는 중입니다.</span>
      </div>);
    }
    if (!detail) {
        return (<div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 flex flex-col items-center justify-center text-center min-h-[16.25rem]">
        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
          <i className="ri-shield-user-line text-slate-400 text-lg"/>
        </div>
        <h3 className="text-sm font-semibold text-slate-600 mb-1">계정을 선택하세요</h3>
        <p className="text-xs text-slate-400 max-w-[12.5rem]">
          목록에서 상세 버튼을 누르면 계정 정보가 표시됩니다.
        </p>
      </div>);
    }
    const statusStyle = statusStyleMap[detail.status] ?? statusStyleMap.INACTIVE;
    const nextStatus = detail.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const userTypeLabel = detail.userType ? userTypeLabels[detail.userType] ?? detail.userType : "-";
    const seniorAccountId = detail.profile?.seniorUserAccountId ?? (detail.userType === "SENIOR" ? detail.userAccountId : null);
    return (<div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <i className="ri-shield-user-line text-sm text-slate-500"/>
          <div>
            <h3 className="text-sm font-semibold text-slate-700">계정 상세</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {detail.loginId} · {userTypeLabel}
            </p>
          </div>
        </div>
        <AdminButton variant="ghost" size="icon" aria-label="계정 상세 닫기" onClick={onClose}>
          <i className="ri-close-line text-sm"/>
        </AdminButton>
      </div>

      <div className="p-4 space-y-4">
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <i className="ri-key-2-line text-blue-600 text-xs"/>
            <h4 className="text-xs font-semibold text-slate-700">계정 정보</h4>
          </div>
          <div className="bg-slate-50 rounded-md p-3 space-y-1">
            <AdminInfoRow dense bordered label="계정 ID" value={detail.userAccountId ?? "-"}/>
            <AdminInfoRow dense bordered label="계정 유형" value={accountTypeLabels[detail.accountType] ?? detail.accountType}/>
            <AdminInfoRow dense bordered label="로그인 ID" value={detail.loginId}/>
            <AdminInfoRow dense bordered label="사용자 유형" value={userTypeLabel}/>
            <AdminInfoRow dense bordered label="소셜 연결" value={yesNoLabels[detail.socialConnectedYn] ?? detail.socialConnectedYn}/>
            <div className="flex items-start justify-between py-1.5 border-b border-slate-50 last:border-b-0">
              <span className="text-xs text-slate-500 shrink-0">상태</span>
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium border ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`}/>
                {statusLabels[detail.status] ?? detail.status}
              </span>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <i className="ri-information-line text-amber-600 text-xs"/>
            <h4 className="text-xs font-semibold text-slate-700">기본 정보</h4>
          </div>
          <div className="bg-slate-50 rounded-md p-3 space-y-1">
            <AdminInfoRow dense bordered label="이름" value={detail.name}/>
            <AdminInfoRow dense bordered label="연락처" value={detail.phone ?? "-"}/>
            <AdminInfoRow dense bordered label="생성일" value={formatDateTime(detail.createdAt)}/>
            <AdminInfoRow dense bordered label="Senior 계정 ID" value={detail.profile?.seniorUserAccountId ?? "-"}/>
            {seniorAccountId != null && (<div className="mt-3 grid grid-cols-1 gap-2">
                <AdminButton type="button" variant="secondary" size="sm" icon="ri-user-heart-line" onClick={() => navigate(`/admin/users/seniors/${seniorAccountId}`)}>
                  Senior 통합 상세 보기
                </AdminButton>
                <AdminButton type="button" variant="soft" size="sm" icon="ri-heart-pulse-line" onClick={() => navigate(`/admin/users/seniors/${seniorAccountId}?tab=care`)}>
                  생활신호 보기
                </AdminButton>
              </div>)}
            {detail.userType === "GUARDIAN" && (<div className="mt-3">
                <AdminButton type="button" variant="secondary" size="sm" icon="ri-group-line" onClick={() => navigate("/admin/family-connections")} className="w-full">
                  가족 연결 관리 열기
                </AdminButton>
              </div>)}
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-t border-slate-100 flex flex-col gap-2">
        <AdminFormField label="변경 사유 유형">
          <AdminSelect value={reasonCode} onValueChange={onReasonCodeChange} disabled={saving}>
            <option value="OPERATOR_REQUEST">운영 요청</option>
            <option value="DATA_CORRECTION">데이터 교정</option>
            <option value="SECURITY_RESPONSE">보안 대응</option>
            <option value="TEST_ROLLBACK">테스트·원복 검증</option>
            <option value="OTHER">기타</option>
          </AdminSelect>
        </AdminFormField>
        <AdminFormField label="변경 사유">
          <AdminTextarea value={reason} onChange={(event) => onReasonChange(event.target.value)} disabled={saving} maxLength={500} rows={3} placeholder="대상과 변경 목적을 10자 이상 입력하세요." />
        </AdminFormField>
        <AdminButton variant="secondary" size="md" icon={nextStatus === "ACTIVE" ? "ri-play-circle-line" : "ri-pause-circle-line"} onClick={() => onStatusChange(nextStatus)} disabled={saving || detail.status === "DELETED" || reason.trim().length < 10} className="w-full">
          {saving ? "저장 중..." : `${statusLabels[nextStatus]}으로 변경`}
        </AdminButton>
        {auditMessage && <p className="rounded-md border border-emerald-100 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-700 break-all">{auditMessage}</p>}
        <AdminButton variant="danger" size="md" icon="ri-file-search-line" disabled={previewLoading} onClick={async () => { setPreviewLoading(true); try { setPreview(await fetchMemberDeletionPreview(detail.userAccountId)); } finally { setPreviewLoading(false); } }} className="w-full">
          {previewLoading ? "영향 조회 중..." : "계정 삭제 영향 보기"}
        </AdminButton>
        {preview && <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800"><p className="font-semibold">{preview.targetLabel} · 삭제 실행 {preview.executable ? "가능" : "차단"}</p>{preview.impacts.map((item) => <p key={item.code}>{item.label}: {item.count}건</p>)}{preview.blockers.map((item) => <p key={item}>차단: {item}</p>)}</div>}
        {preview?.executable && <>
          <AdminFormField label="삭제 확인">
            <AdminTextInput tone="danger" value={confirmationText} onChange={(event) => setConfirmationText(event.target.value)} disabled={saving} placeholder={`계정 ID ${detail.userAccountId} 입력`} />
          </AdminFormField>
          <AdminButton variant="danger" size="md" icon="ri-delete-bin-line" disabled={saving || reason.trim().length < 10 || confirmationText.trim() !== String(detail.userAccountId)} onClick={() => onDelete(confirmationText.trim())} className="w-full">
            {saving ? "처리 중..." : "계정 영구 삭제"}
          </AdminButton>
        </>}
        <p className="text-xs text-amber-700 text-center">삭제 영향 재확인, 10자 이상 사유, 대상 ID 확인이 모두 일치해야 실행됩니다.</p>
      </div>
    </div>);
};
export default AccountDetailPanel;
